import { useEffect, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/config/firebase'
import type { ProjectDoc } from '@/types/project'
import type { ProjectRecord } from './useProjects'

export type AssignmentField = 'supplierUid' | 'clientUid'

interface ProjectsState {
  projects: ProjectRecord[]
  loading: boolean
  error: string | null
}

/**
 * Realtime list of projects assigned to `uid` via `field` (supplierUid or
 * clientUid). The equality filter makes the query rules-safe: it only ever
 * returns docs the user is allowed to read (firestore.rules isAssignedTo).
 */
export function useAssignedProjects(field: AssignmentField, uid: string): ProjectsState {
  const [state, setState] = useState<ProjectsState>({ projects: [], loading: true, error: null })

  useEffect(() => {
    // An empty uid simply matches no documents (returns an empty list).
    const assignedQuery = query(collection(db, 'projects'), where(field, '==', uid))
    const unsubscribe = onSnapshot(
      assignedQuery,
      (snapshot) => {
        const projects = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as ProjectDoc),
        }))
        setState({ projects, loading: false, error: null })
      },
      (error) => setState({ projects: [], loading: false, error: error.message }),
    )
    return unsubscribe
  }, [field, uid])

  return state
}
